import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import DataPageLayout from '../components/DataPageLayout';
import { getEmailLog, listEmailLogs } from '../services/emailLogsService';
import '../components/DataPage.css';
import './Dashboard.css';

function formatCount(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return Number(value).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
}

function formatBody(log) {
  if (log.body_text) {
    return log.body_text;
  }

  if (log.body_html) {
    return log.body_html.replace(/<[^>]*>/g, '');
  }

  return null;
}

function DetailField({ label, value, fullWidth = false }) {
  return (
    <div className={`data-page__detail-field${fullWidth ? ' data-page__detail-field--full' : ''}`}>
      <span>{label}</span>
      <p>{value ?? '—'}</p>
    </div>
  );
}

function EmailLogDetailModal({ logId, onClose }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadLog() {
      setLoading(true);

      try {
        const data = await getEmailLog(logId);
        if (isMounted) {
          setLog(data);
        }
      } catch (err) {
        if (isMounted) {
          toast.error(err.message);
          setLog(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadLog();

    return () => {
      isMounted = false;
    };
  }, [logId]);

  const body = log ? formatBody(log) : null;

  return (
    <div className="data-page__modal-backdrop" onClick={onClose}>
      <div
        className="data-page__modal data-page__modal--detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-log-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="email-log-detail-title">Email details</h3>

        {loading && <p className="data-page__detail-loading">Loading log details...</p>}

        {log && (
          <>
            <div className="data-page__detail-grid">
              <DetailField label="ID" value={log.id} />
              <DetailField
                label="Status"
                value={
                  <span className={`data-page__status data-page__status--${log.status}`}>
                    {log.status}
                  </span>
                }
              />
              <DetailField label="Sender" value={log.sender} />
              <DetailField label="Provider" value={log.provider || '—'} />
              <DetailField label="Recipient" value={log.recipient} fullWidth />
              <DetailField label="Subject" value={log.subject} fullWidth />
              <DetailField label="Message ID" value={log.message_id} fullWidth />
              <DetailField label="Sender ID" value={log.sender_id} />
              <DetailField
                label="Attachment"
                value={log.has_attachment ? 'Yes' : 'No'}
              />
              <DetailField label="Sent At" value={formatDate(log.created_at)} />
              {log.error_message && (
                <DetailField label="Error" value={log.error_message} fullWidth />
              )}
            </div>

            {body && (
              <div className="data-page__detail-body">
                <span>Body</span>
                <pre>{body}</pre>
              </div>
            )}
          </>
        )}

        <div className="data-page__modal-actions">
          <button type="button" className="data-page__modal-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailLogs() {
  const requestIdRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, sent: 0, failed: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedLogId, setSelectedLogId] = useState(null);

  const loadLogs = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!hasLoadedRef.current) {
      setLoading(true);
    }

    try {
      const data = await listEmailLogs({ page, limit });
      if (requestId !== requestIdRef.current) {
        return;
      }

      const nextSummary = {
        total: Number(data?.summary?.total) || 0,
        sent: Number(data?.summary?.sent) || 0,
        failed: Number(data?.summary?.failed) || 0,
      };
      const nextPagination = {
        page: Number(data?.pagination?.page) || page,
        limit: Number(data?.pagination?.limit) || limit,
        total: Number(data?.pagination?.total ?? data?.summary?.total) || 0,
        totalPages: Number(data?.pagination?.totalPages) || 0,
      };

      setLogs(Array.isArray(data?.rows) ? data.rows : []);
      setSummary(nextSummary);
      setPagination(nextPagination);
      hasLoadedRef.current = true;
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      toast.error(err.message);
      setLogs([]);
      setSummary({ total: 0, sent: 0, failed: 0 });
      setPagination({ page, limit, total: 0, totalPages: 0 });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, limit]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const successRate = summary.total
    ? `${Math.round((summary.sent / summary.total) * 100)}%`
    : '—';

  const metrics = [
    {
      title: 'Total Logs',
      value: loading && !hasLoadedRef.current ? '...' : formatCount(summary.total),
      subtext: 'All send records',
      highlight: true,
    },
    {
      title: 'Sent',
      value: loading && !hasLoadedRef.current ? '...' : formatCount(summary.sent),
      subtext: 'Sent successfully',
    },
    {
      title: 'Failed',
      value: loading && !hasLoadedRef.current ? '...' : formatCount(summary.failed),
      subtext: 'Failed to send',
    },
    {
      title: 'Success Rate',
      value: loading && !hasLoadedRef.current ? '...' : successRate,
      subtext: 'Of all logs',
      highlight: true,
    },
  ];

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'sender', label: 'Sender', className: 'data-page__cell-truncate' },
    {
      key: 'provider',
      label: 'Provider',
      render: (row) => row.provider || '—',
    },
    { key: 'recipient', label: 'Recipient', className: 'data-page__cell-truncate' },
    { key: 'subject', label: 'Subject', className: 'data-page__cell-truncate' },
    {
      key: 'has_attachment',
      label: 'Attachment',
      render: (row) => (row.has_attachment ? 'Yes' : 'No'),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <span className={`data-page__status data-page__status--${row.status}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Sent At',
      render: (row) => formatDate(row.created_at),
    },
  ];

  return (
    <>
      <DataPageLayout
        title="Email Logs"
        subtitle="See which emails were sent and whether they went through."
        metrics={metrics}
        loading={loading}
        onRefresh={loadLogs}
        onRowClick={(row) => setSelectedLogId(row.id)}
        columns={columns}
        rows={logs}
        emptyMessage="No email logs found."
        serverPagination
        totalRows={pagination.total}
        page={page}
        rowsPerPage={limit}
        onPageChange={(nextPage) => {
          if (nextPage !== page) {
            setPage(nextPage);
          }
        }}
        onRowsPerPageChange={(nextLimit) => {
          if (nextLimit === limit) {
            return;
          }

          setLimit(nextLimit);
          setPage(1);
        }}
      />

      {selectedLogId && (
        <EmailLogDetailModal logId={selectedLogId} onClose={() => setSelectedLogId(null)} />
      )}
    </>
  );
}

export default EmailLogs;
